// animations/loop-animations.ts
import gsap from 'gsap';

import { AnimationDynamicSettings, AnimationType } from './animation/animations.model';
import { baseBounce } from './common-base-animations';
import { cleanup } from './dot-cms-aniamtion.helper';

export const loopAnimations: Partial<
  Record<AnimationType, (s: AnimationDynamicSettings, el: Element, opts: gsap.TweenVars) => gsap.core.Tween>
> = {
  // Use common base animations with loop options
  flip: (s, el, opts) => {
    cleanup(el);
    if ((s.orientation ?? 'horizontal') === 'horizontal') {
      return gsap.to(el, { rotationY: 180, repeat: -1, yoyo: true, repeatDelay: s.repeat_delay, ...opts });
    }
    return gsap.to(el, { rotationX: 180, repeat: -1, yoyo: true, repeatDelay: s.repeat_delay, ...opts });
  },
  flap: (s, el, opts) => {
    cleanup(el);
    let angle = (s.intensity ?? 1) * 30;
    let origin = '50% 0%'; // default: top
    let axis = 'rotationX'; // default: flap vertically

    switch ((s.direction || 'up').toLowerCase()) {
      case 'up':
        origin = '50% 0%'; // hinge at top
        axis = 'rotationX';
        break;
      case 'down':
        origin = '50% 100%'; // hinge at bottom
        axis = 'rotationX';
        angle = -angle;
        break;
      case 'left':
        origin = '0% 50%'; // hinge at left
        axis = 'rotationY';
        break;
      case 'right':
        origin = '100% 50%'; // hinge at right
        axis = 'rotationY';
        angle = -angle;
        break;
    }
    return gsap.to(el, {
      [axis]: angle,
      repeat: -1,
      repeatDelay: s.repeat_delay ?? 0,
      yoyo: true,
      transformOrigin: origin,
      ...opts,
    });
  },
  bounce: (s, el, opts) =>
    baseBounce(el, { y: -(40 * (s.intensity || 1)), repeat: -1, yoyo: true, ...opts, ease: 'back.out(1.7)' }),

  // Loop-specific animations
  breathe: (s, el, opts) => {
    cleanup(el);
    if (s.orientation === 'horizontal') {
      return gsap.to(el, {
        scaleX: s.breathe_scale ?? 1.2,
        repeat: -1,
        yoyo: true,
        transformOrigin: 'center',
        ...opts,
      });
    }
    return gsap.to(el, { scaleY: s.breathe_scale ?? 1.2, repeat: -1, yoyo: true, transformOrigin: 'center', ...opts });
  },

  pulse: (s, el, opts) => {
    cleanup(el);
    let intensity = 1 + s.intensity * 0.5;
    return gsap.to(el, { scale: intensity, repeat: -1, ease:'power1.inOut', yoyo: true, ...opts });
  },

  spin: (s, el, opts) => {
    cleanup(el);
    const rotDir = s.direction == 'counter-clockwise' ? -360 : 360;
    return gsap.to(el, {
      rotation: rotDir,
      repeat: -1,
      transformOrigin: '50% 50%',
      ...opts,
    });
  },

  poke: (s, el, opts) => {
    cleanup(el);
    const dist = (s.intensity ?? 0.5) * 20;
    let prop, fromVal, toVal;
    switch (s.direction) {
      case 'left':
        prop = 'x';
        fromVal = 0;
        toVal = -dist;
        break;
      case 'right':
        prop = 'x';
        fromVal = 0;
        toVal = dist;
        break;
      case 'up':
        prop = 'y';
        fromVal = 0;
        toVal = -dist;
        break;
      case 'down':
        prop = 'y';
        fromVal = 0;
        toVal = dist;
        break;
      default:
        prop = 'x';
        fromVal = 0;
        toVal = dist;
    }
    return gsap.fromTo(
      el,
      { [prop]: fromVal },
      {
        [prop]: toVal,
        duration: (s.duration ?? 1) / 2,
        ease: 'power1.inOut',
        yoyo: true,
        repeat: -1,
        repeatDelay: s.repeat_delay ?? 0,
        ...opts,
      }
    );
  },

  flash: (s, el, opts) =>
    gsap.to(el, {
      opacity: 0,
      repeat: -1,
      repeatDelay: s.repeat_delay ?? 0,
      yoyo: true,
      ...opts,
    }),
  swing: (s, el, opts) => {
    cleanup(el);
    let angle = (s.intensity ?? 0.5) * 15;
    const dir = (s.direction || 'right').toLowerCase();

    // adjust angle direction
    if (dir === 'left' || dir === 'up') {
      angle = -angle;
    }

    // pick pivot point
    let origin = '50% 50%';
    switch (dir) {
      case 'left':
        origin = '0% 50%';
        break;
      case 'right':
        origin = '100% 50%';
        break;
      case 'up':
        origin = '50% 0%';
        break;
      case 'down':
        origin = '50% 100%';
        break;
    }

    return gsap.to(el, {
      rotation: angle,
      duration: s.duration ?? 1,
      ease: s.easing ?? 'power1.inOut',
      repeat: -1,
      repeatDelay: s.repeat_delay ?? 0,
      yoyo: true,
      transformOrigin: origin,
      ...opts,
    });
  },

  rubber: (s, el, opts) => {
    cleanup(el);
    return gsap.to(el, {
      scaleX: 1 + 0.25 * (s.intensity ?? 0.5),
      scaleY: 1 - 0.25 * (s.intensity ?? 0.5),
      ease: 'elastic.inOut(1, 0.3)',
      repeat: -1,
      repeatDelay: s.repeat_delay ?? 0,
      yoyo: true,
      transformOrigin: 'center',
      ...opts,
    });
  },

  cross: (s, el, opts) => {
    const dir = (s.direction || 'left').toLowerCase();
    const boxRect = el.getBoundingClientRect();
    const vw = boxRect.width;
    const vh = boxRect.height;

    let prop = 'x';
    let fromVal = 0,
      toVal = 0;

    switch (dir) {
      case 'left':
        prop = 'x';
        fromVal = vw / 2 + boxRect.width; // start outside right
        toVal = -(vw / 2 + boxRect.width); // move fully left
        break;

      case 'right':
        prop = 'x';
        fromVal = -(vw / 2 + boxRect.width); // start outside left
        toVal = vw / 2 + boxRect.width; // move fully right
        break;

      case 'up':
        prop = 'y';
        fromVal = vh / 2 + boxRect.height; // start below
        toVal = -(vh / 2 + boxRect.height); // move above
        break;

      case 'down':
        prop = 'y';
        fromVal = -(vh / 2 + boxRect.height); // start above
        toVal = vh / 2 + boxRect.height; // move below
        break;
    }

    return gsap.fromTo(
      el,
      { [prop]: fromVal },
      {
        [prop]: toVal,
        ease: 'power1.inOut',
        repeat: -1, // loop endlessly
        repeatDelay: s.repeat_delay || 0,
        ...opts,
      }
    );
  },

  jello: (s, el, opts) =>
    gsap.to(el, {
      x: 10 * (s.intensity || 1),
      y: 10 * (s.intensity || 1),
      ease: 'power1.inOut',
      yoyo: true,
      yoyoEase: 'power1.inOut',
      repeat: -1,
      ...opts,
    }),

  wiggle: (s, el, opts) => {
    const dist = 30 * (s.intensity || 1); // max shake distance

    return gsap.to(el, {
      x: () => gsap.utils.random(-dist, dist),
      y: () => gsap.utils.random(-dist, dist),
      ease: 'none',
      repeat: -1,
      repeatDelay: s.repeat_delay || 0,
      repeatRefresh: true, // pick new random position every frame
      transformOrigin: '50% 50%',
      ...opts,
    });
  },
};
